import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { FiX, FiPlus, FiTrash2 } from 'react-icons/fi';
import ModalFooter from '../common/ModalFooter';
import { generateTagKey, generateTagValue } from '../../utils/tagUtils';

const AddQuestionModal = ({ isOpen, onClose, onSubmit, loading, initialData = null }) => {
  const [type, setType] = useState('single_select');
  const [text, setText] = useState('');
  const [tagKey, setTagKey] = useState('');
  const [options, setOptions] = useState([]);
  const [newOption, setNewOption] = useState('');
  const [isTagKeyEdited, setIsTagKeyEdited] = useState(false);

  useEffect(() => {
    if (initialData) {
      setType(initialData.question_type);
      setText(initialData.question_text);
      setTagKey(initialData.tag_key || '');
      setOptions(initialData.options || []);
      setIsTagKeyEdited(true);
    } else {
      setType('single_select');
      setText('');
      setTagKey('');
      setOptions([]);
      setIsTagKeyEdited(false);
    }
  }, [initialData, isOpen]);

  const handleTextChange = (val) => {
    setText(val);
    if (!isTagKeyEdited && type === 'single_select') {
      setTagKey(generateTagKey(val));
    }
  };

  const addOption = () => {
    if (!newOption.trim()) return;
    const label = newOption.trim();
    const value = generateTagValue(label);
    
    if (options.find(o => o.value === value)) {
      alert("This option already exists.");
      return;
    }

    setOptions([...options, { label, value }]);
    setNewOption('');
  };

  if (!isOpen) return null;

  const isValid = text.trim() && (type === 'text' || options.length >= 2);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-rutabaga-dark/40 backdrop-blur-sm">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white w-full max-w-2xl rounded-lg p-10 shadow-2xl relative border-t-4 border-rutabaga-pink overflow-y-auto max-h-[90vh]"
      >
        <button onClick={onClose} className="absolute top-6 right-6 text-gray-400 hover:text-rutabaga-dark transition-colors p-1">
          <SafeIcon icon={FiX} className="text-xl" />
        </button>

        <h2 className="text-3xl font-black text-rutabaga-dark tracking-tight mb-8">
          {initialData ? 'Edit Question' : 'Add Screener Question'}
        </h2>

        <div className="space-y-8">
          {/* Type Selector */}
          <div>
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 ml-1">Question Purpose</label>
            <div className="grid grid-cols-2 gap-4">
              <button 
                onClick={() => setType('single_select')}
                className={`p-4 rounded-md border-2 text-left transition-all ${type === 'single_select' ? 'border-rutabaga-pink bg-rutabaga-pink/5' : 'border-gray-100 hover:border-gray-200'}`}
              >
                <p className={`font-black text-sm ${type === 'single_select' ? 'text-rutabaga-pink' : 'text-gray-700'}`}>Segmentation</p>
                <p className="text-xs text-gray-400 mt-1">Single-select for recruitment health and analysis.</p>
              </button>
              <button 
                onClick={() => setType('text')}
                className={`p-4 rounded-md border-2 text-left transition-all ${type === 'text' ? 'border-rutabaga-pink bg-rutabaga-pink/5' : 'border-gray-100 hover:border-gray-200'}`}
              >
                <p className={`font-black text-sm ${type === 'text' ? 'text-rutabaga-pink' : 'text-gray-700'}`}>Additional</p>
                <p className="text-xs text-gray-400 mt-1">Open text for general participant context.</p>
              </button>
            </div>
          </div>

          {/* Question Text */}
          <div>
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Question Text</label>
            <input
              type="text"
              className="rutabaga-input"
              placeholder="e.g., Which region are you based in?"
              value={text}
              onChange={(e) => handleTextChange(e.target.value)}
            />
          </div>

          {type === 'single_select' && (
            <>
              {/* Tag Key */}
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1 flex justify-between">
                  <span>Data Tag Key (Auto-generated)</span>
                  <span className="text-rutabaga-pink italic lowercase">Internal identifier</span>
                </label>
                <input
                  type="text"
                  className="rutabaga-input bg-gray-50/50"
                  placeholder="tag_key"
                  value={tagKey}
                  onChange={(e) => { setTagKey(generateTagValue(e.target.value)); setIsTagKeyEdited(true); }}
                />
              </div>

              {/* Option Builder */}
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Response Options (Min 2)</label>
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    className="rutabaga-input"
                    placeholder="Add an option..."
                    value={newOption}
                    onChange={(e) => setNewOption(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addOption())}
                  />
                  <button 
                    type="button" 
                    onClick={addOption}
                    className="bg-rutabaga-dark text-white px-4 rounded-md hover:bg-black transition-colors"
                  >
                    <SafeIcon icon={FiPlus} />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {options.map((opt, i) => (
                    <div key={i} className="flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-full group">
                      <span className="text-xs font-bold text-gray-700">{opt.label}</span>
                      <button 
                        onClick={() => setOptions(options.filter((_, idx) => idx !== i))}
                        className="text-gray-400 hover:text-rutabaga-pink transition-colors"
                      >
                        <SafeIcon icon={FiX} />
                      </button>
                    </div>
                  ))}
                  {options.length < 2 && (
                    <p className="text-[10px] text-amber-500 font-bold uppercase italic mt-1">Please add at least {2 - options.length} more options</p>
                  )}
                </div>
              </div>
            </>
          )}

          <ModalFooter 
            primaryLabel={initialData ? 'Save Changes' : 'Add Question'}
            onSecondary={onClose}
            secondaryLabel="Cancel"
            loading={loading}
            disabled={!isValid}
            onPrimary={() => onSubmit({ question_text: text, question_type: type, tag_key: type === 'single_select' ? tagKey : null, options })}
          />
        </div>
      </motion.div>
    </div>
  );
};

export default AddQuestionModal;